import {
  type CollectionReference,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  Timestamp,
  collection,
  orderBy,
  query,
} from 'firebase/firestore'

import { callable } from '@/lib/firebase/callable'
import { getFirebaseFirestore } from '@/lib/firebase/app'
import { type Subscription, subscribeCollection } from '@/lib/firebase/subscribe'
import { isPageType } from '@/types/plan'
import { toContentStatus } from '@/lib/content/content-status'
import {
  CONTENT_CHANNELS,
  CONTENT_SHAPES,
  REVISION_AUTHORS,
  REVISION_REASONS,
  type ArticleDoc,
  type ArticleLinks,
  type ArticlePlan,
  type ArticleChecks,
  type ArticleHandoff,
  type ArticleSectionTrace,
  SLUG_SOURCES,
  FINDING_CATEGORIES,
  type FindingCategory,
  type ReadingLevel,
  SECTION_TESTS,
  type SectionTest,
  type ContentChannel,
  type ContentItem,
  type ContentRevision,
  type ContentShape,
  type LintFinding,
  type NextOpenSlot,
  type PostDesign,
  type PostMedia,
  type PostResults,
  type PostSlide,
  SLIDE_ROLES,
  type SlideRole,
  type RevisionAuthor,
  type RevisionReason,
} from '@/types/content'

function postsPath(workspaceId: string, brandId: string): CollectionReference<DocumentData> {
  return collection(getFirebaseFirestore(), 'workspaces', workspaceId, 'brands', brandId, 'posts')
}

function revisionsPath(
  workspaceId: string,
  brandId: string,
  postId: string,
): CollectionReference<DocumentData> {
  return collection(postsPath(workspaceId, brandId), postId, 'revisions')
}

function postAnalyticsPath(
  workspaceId: string,
  brandId: string,
): CollectionReference<DocumentData> {
  return collection(
    getFirebaseFirestore(),
    'workspaces',
    workspaceId,
    'brands',
    brandId,
    'postAnalytics',
  )
}

function articlesPath(workspaceId: string, brandId: string): CollectionReference<DocumentData> {
  return collection(
    getFirebaseFirestore(),
    'workspaces',
    workspaceId,
    'brands',
    brandId,
    'articles',
  )
}

function toPostMedia(value: unknown): PostMedia | null {
  if (typeof value !== 'object' || value === null) return null
  const units = (value as { units?: unknown }).units
  if (!Array.isArray(units)) return null
  return {
    units: units.flatMap((unit: unknown): PostSlide[] => {
      if (typeof unit !== 'object' || unit === null) return []
      const { role, block, components } = unit as Record<string, unknown>
      if (!SLIDE_ROLES.includes(role as SlideRole) || typeof block !== 'string') return []
      const texts = Array.isArray(components) ? components : []
      return [
        {
          role: role as SlideRole,
          block,
          components: texts.flatMap((component: unknown) => {
            if (typeof component !== 'object' || component === null) return []
            const { type, text } = component as Record<string, unknown>
            return typeof type === 'string' && typeof text === 'string' ? [{ type, text }] : []
          }),
        },
      ]
    }),
  }
}

function asContentShape(value: unknown): ContentShape {
  return typeof value === 'string' && (CONTENT_SHAPES as readonly string[]).includes(value)
    ? (value as ContentShape)
    : 'post'
}

function asContentChannels(value: unknown): ContentChannel[] {
  return typeof value === 'string' && (CONTENT_CHANNELS as readonly string[]).includes(value)
    ? [value as ContentChannel]
    : []
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) return value.toDate()
  if (typeof value === 'string' && value !== '') return new Date(value)
  return undefined
}

function toRequiredIsoDate(value: unknown): Date {
  return typeof value === 'string' && value !== '' ? new Date(value) : new Date(0)
}

function toPostDesign(value: unknown): PostDesign | null {
  if (typeof value !== 'object' || value === null) return null
  const design = value as DocumentData
  if (typeof design.designId !== 'string') return null
  return {
    designId: design.designId,
    pageCount: typeof design.pageCount === 'number' ? design.pageCount : 0,
    renderVersion: typeof design.renderVersion === 'number' ? design.renderVersion : 0,
    attachedAt: toRequiredIsoDate(design.attachedAt),
  }
}

function toContentItem(
  brandId: string,
  snapshot: QueryDocumentSnapshot<DocumentData>,
): ContentItem {
  const data = snapshot.data()
  const body = String(data.body ?? '')

  return {
    id: snapshot.id,
    shape: asContentShape(data.shape),
    title: typeof data.title === 'string' ? data.title : '',
    copy: body,
    status: toContentStatus(data.status),
    channels: asContentChannels(data.channelSlug),
    scheduledAt: toOptionalDate(data.publishAt) ?? null,
    design: toPostDesign(data.design),
    sourceId: typeof data.sourceId === 'string' ? data.sourceId : null,
    brandId,
    runId: typeof data.runId === 'string' ? data.runId : null,
    topicId: typeof data.topicId === 'string' ? data.topicId : null,
    beliefSlug: typeof data.beliefSlug === 'string' ? data.beliefSlug : null,
    score: typeof data.score === 'number' ? data.score : null,
    reasons: Array.isArray(data.reasons)
      ? data.reasons.filter((reason: unknown) => typeof reason === 'string')
      : [],
    media: toPostMedia(data.media),
    publishedAt: toOptionalDate(data.publishedAt),
    publishedUrl: typeof data.publishedUrl === 'string' ? data.publishedUrl : undefined,
    publishingError: typeof data.publishingError === 'string' ? data.publishingError : undefined,
    currentRevision: typeof data.currentRevision === 'string' ? data.currentRevision : undefined,
    updatedAt: toOptionalDate(data.updatedAt),
  }
}

export interface SubscribePostsInput {
  workspaceId: string
  brandId: string
}

export function subscribeToPosts(input: SubscribePostsInput): Subscription<ContentItem[]> {
  return subscribeCollection(postsPath(input.workspaceId, input.brandId), (snapshot) =>
    snapshot.docs.map((doc) => toContentItem(input.brandId, doc)),
  )
}

function toPostResults(snapshot: QueryDocumentSnapshot<DocumentData>): PostResults {
  const data = snapshot.data()
  const base = {
    postId: snapshot.id,
    intent: typeof data.intent === 'string' ? data.intent : undefined,
    lastSyncedAt: toRequiredIsoDate(data.lastSyncedAt),
  }

  if (data.syncPending === true) {
    return { ...base, status: 'pending' }
  }

  return {
    ...base,
    status: 'measured',
    impressions: typeof data.impressions === 'number' ? data.impressions : 0,
    reach: typeof data.reach === 'number' ? data.reach : 0,
    likes: typeof data.likes === 'number' ? data.likes : 0,
    comments: typeof data.comments === 'number' ? data.comments : 0,
    shares: typeof data.shares === 'number' ? data.shares : 0,
    saves: typeof data.saves === 'number' ? data.saves : 0,
    clicks: typeof data.clicks === 'number' ? data.clicks : 0,
    views: typeof data.views === 'number' ? data.views : 0,
    engagementRate: typeof data.engagementRate === 'number' ? data.engagementRate : 0,
  }
}

export interface SubscribePostResultsInput {
  workspaceId: string
  brandId: string
}

export function subscribeToPostResults(
  input: SubscribePostResultsInput,
): Subscription<PostResults[]> {
  return subscribeCollection(postAnalyticsPath(input.workspaceId, input.brandId), (snapshot) =>
    snapshot.docs.map((doc) => toPostResults(doc)),
  )
}

function asRevisionAuthor(value: unknown): RevisionAuthor {
  return typeof value === 'string' && (REVISION_AUTHORS as readonly string[]).includes(value)
    ? (value as RevisionAuthor)
    : 'raldo'
}

function asRevisionReason(value: unknown): RevisionReason {
  return typeof value === 'string' && (REVISION_REASONS as readonly string[]).includes(value)
    ? (value as RevisionReason)
    : 'generated'
}

function toContentRevision(
  postId: string,
  snapshot: QueryDocumentSnapshot<DocumentData>,
): ContentRevision {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    postId,
    parentId: typeof data.parentId === 'string' ? data.parentId : null,
    body: String(data.body ?? ''),
    author: asRevisionAuthor(data.author),
    reason: asRevisionReason(data.reason),
    instruction: typeof data.instruction === 'string' ? data.instruction : undefined,
    createdAt: toRequiredIsoDate(data.createdAt),
  }
}

export interface SubscribePostRevisionsInput {
  workspaceId: string
  brandId: string
  postId: string
}

export function subscribeToPostRevisions(
  input: SubscribePostRevisionsInput,
): Subscription<ContentRevision[]> {
  return subscribeCollection(
    query(
      revisionsPath(input.workspaceId, input.brandId, input.postId),
      orderBy('createdAt', 'asc'),
    ),
    (snapshot) => snapshot.docs.map((doc) => toContentRevision(input.postId, doc)),
  )
}

function toLintFindings(value: unknown): LintFinding[] {
  if (!Array.isArray(value)) return []
  const findings: LintFinding[] = []
  for (const item of value) {
    if (typeof item !== 'object' || item === null) continue
    const { rule, severity, message } = item as Record<string, unknown>
    if (typeof rule !== 'string' || typeof message !== 'string') continue
    findings.push({ rule, severity: severity === 'hard' ? 'hard' : 'warning', message })
  }
  return findings
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function toArticlePlan(value: unknown): ArticlePlan {
  const plan = (typeof value === 'object' && value !== null ? value : {}) as DocumentData
  const brandLine = plan.brandLine as DocumentData | null | undefined
  return {
    title: text(plan.title),
    h1: text(plan.h1),
    promise: text(plan.promise),
    metaDescription: text(plan.metaDescription),
    keyword: typeof plan.keyword === 'string' ? plan.keyword : null,
    definition: text(plan.definition),
    definitionFrom: text(plan.definitionFrom),
    brandLine: brandLine
      ? {
          section: text(brandLine.section),
          place: text(brandLine.place),
          text: text(brandLine.text),
        }
      : null,
    sections: records(plan.sections).map((section) => ({
      id: text(section.id),
      kind: text(section.kind),
      name: text(section.name),
      heading: typeof section.heading === 'string' ? section.heading : null,
      point: typeof section.point === 'string' ? section.point : null,
      keyFacts: section.keyFacts === true,
      cta: section.cta === true,
    })),
    nextStepId: typeof plan.nextStepId === 'string' ? plan.nextStepId : null,
    topicLinkIds: strings(plan.topicLinkIds),
    sources: records(plan.sources).map((source) => ({
      url: text(source.url),
      by: text(source.by),
    })),
  }
}

function records(value: unknown): DocumentData[] {
  return Array.isArray(value)
    ? value.filter((item): item is DocumentData => typeof item === 'object' && item !== null)
    : []
}

function count(value: unknown): number {
  return typeof value === 'number' ? value : 0
}

function toReadingLevel(value: unknown): ReadingLevel {
  const level = (typeof value === 'object' && value !== null ? value : {}) as DocumentData
  return {
    words: count(level.words),
    sentences: count(level.sentences),
    wordsPerSentence: count(level.wordsPerSentence),
    longWords: count(level.longWords),
    ease: count(level.ease),
    grade: count(level.grade),
  }
}

function toSectionTests<T>(value: unknown, of: (entry: unknown) => T): Record<SectionTest, T> {
  const tests = (typeof value === 'object' && value !== null ? value : {}) as DocumentData
  return Object.fromEntries(SECTION_TESTS.map((test) => [test, of(tests[test])])) as Record<
    SectionTest,
    T
  >
}

function toCounts(value: unknown): Record<string, number> {
  const counts = (typeof value === 'object' && value !== null ? value : {}) as DocumentData
  return Object.fromEntries(Object.entries(counts).map(([key, entry]) => [key, count(entry)]))
}

function toArticleChecks(value: unknown): ArticleChecks | null {
  if (typeof value !== 'object' || value === null) return null
  const checks = value as DocumentData
  return {
    findings: records(checks.findings).flatMap((finding) =>
      (FINDING_CATEGORIES as readonly string[]).includes(finding.category)
        ? [
            {
              section: text(finding.section),
              sentence: text(finding.sentence),
              category: finding.category as FindingCategory,
              why: text(finding.why),
            },
          ]
        : [],
    ),
    lint: toLintFindings(checks.lint),
    sections: records(checks.sections).map((section) => ({
      id: text(section.id),
      heading: text(section.heading),
      words: count(section.words),
      wordsBy: toCounts(section.wordsBy),
      level: toReadingLevel(section.level),
      tests: toSectionTests(section.tests, (entry) => entry === true),
      tableRowsWithoutSource: strings(section.tableRowsWithoutSource),
    })),
    passed: toSectionTests(checks.passed, (entry) => {
      const score = (typeof entry === 'object' && entry !== null ? entry : {}) as DocumentData
      return { passed: count(score.passed), of: count(score.of) }
    }),
    share: toCounts(checks.share),
    level: toReadingLevel(checks.level),
    nearCopies: records(checks.nearCopies).map((copy) => ({
      section: text(copy.section),
      sentence: text(copy.sentence),
      sources: strings(copy.sources),
      shared: text(copy.shared),
    })),
  }
}

function toArticleHandoff(value: unknown): ArticleHandoff | null {
  if (typeof value !== 'object' || value === null) return null
  const handoff = value as DocumentData
  const slugFrom = SLUG_SOURCES.find((source) => source === handoff.slugFrom) ?? 'question'
  const faqSchema =
    typeof handoff.faqSchema === 'object' && handoff.faqSchema !== null
      ? (handoff.faqSchema as Record<string, unknown>)
      : null
  return {
    seoTitle: text(handoff.seoTitle),
    metaDescription: text(handoff.metaDescription),
    slug: text(handoff.slug),
    slugFrom,
    url: text(handoff.url),
    faqSchema,
    placeholders: strings(handoff.placeholders),
    sources: records(handoff.sources).map((source) => ({
      id: text(source.id),
      by: text(source.by),
      url: text(source.url),
    })),
    words: count(handoff.words),
  }
}

function toSectionTrace(value: unknown): ArticleSectionTrace[] | null {
  if (!Array.isArray(value)) return null
  const link = (entry: DocumentData) => (typeof entry.url === 'string' ? entry.url : null)
  return records(value).map((section) => ({
    id: text(section.id),
    heading: text(section.heading),
    sources: records(section.sources).map((source) => ({
      by: text(source.by),
      fact: text(source.fact),
      url: link(source),
    })),
    references: records(section.references).map((reference) => ({
      title: text(reference.title),
      url: link(reference),
    })),
    influencedBy: records(section.influencedBy).map((piece) => ({
      source: text(piece.source),
      text: text(piece.text),
      url: link(piece),
      how: text(piece.how),
    })),
    gains: records(section.gains).map((gain) => ({ kind: text(gain.kind), text: text(gain.text) })),
    yourAnswers: records(section.yourAnswers).map((answer) => ({
      ask: text(answer.ask),
      answer: text(answer.answer),
    })),
  }))
}

function toArticleDoc(snapshot: DocumentSnapshot<DocumentData>): ArticleDoc | undefined {
  const data = snapshot.data()
  if (data === undefined) return undefined
  if (typeof data.postId !== 'string' || data.postId === '') return undefined

  return {
    postId: data.postId,
    pageId: text(data.pageId),
    topicId: text(data.topicId),
    type: isPageType(data.type) ? data.type : 'supporting',
    plan: toArticlePlan(data.plan),
    answer: text(data.answer),
    sections: records(data.sections).map((section) => ({
      id: text(section.id),
      heading: text(section.heading),
      text: text(section.text),
      keyFacts: strings(section.keyFacts),
      words: count(section.words),
    })),
    body: text(data.body),
    wordCount: count(data.wordCount),
    checks: toArticleChecks(data.checks),
    trace: toSectionTrace(data.trace),
    handoff: toArticleHandoff(data.handoff),
    publishedUrl: typeof data.publishedUrl === 'string' ? data.publishedUrl : undefined,
    verifiedAt: toOptionalDate(data.verifiedAt),
    createdAt: toRequiredIsoDate(data.createdAt),
    updatedAt: toRequiredIsoDate(data.updatedAt),
  }
}

export interface SubscribeArticlesInput {
  workspaceId: string
  brandId: string
}

/** Live view of every article in the brand at once. */
export function subscribeToArticles(input: SubscribeArticlesInput): Subscription<ArticleDoc[]> {
  return subscribeCollection(articlesPath(input.workspaceId, input.brandId), (snapshot) => {
    const articles: ArticleDoc[] = []
    for (const articleDoc of snapshot.docs) {
      const article = toArticleDoc(articleDoc)
      if (article) articles.push(article)
    }
    return articles
  })
}

export interface EditPostBodyInput {
  postId: string
  body: string
}

export interface EditPostBodyResult {
  revisionId: string
}

export const editPostBody = callable<EditPostBodyInput, EditPostBodyResult>('editPostBody')

export interface SchedulePostInput {
  postId: string
  publishAt?: string
}

export const schedulePost = callable<SchedulePostInput, void>('schedulePost')

export interface PublishPostNowInput {
  postId: string
}

export const publishPostNow = callable<PublishPostNowInput, void>('publishPostNow')

export interface ReschedulePostInput {
  postId: string
  publishAt: string
}

export const reschedulePost = callable<ReschedulePostInput, void>('reschedulePost')

export interface SetPostPublishAtInput {
  postId: string
  publishAt: string | null
}

export const setPostPublishAt = callable<SetPostPublishAtInput, void>('setPostPublishAt')

export interface CancelScheduleInput {
  postId: string
}

export const cancelSchedule = callable<CancelScheduleInput, void>('cancelSchedule')

export interface DeletePostInput {
  postId: string
}

export const deletePost = callable<DeletePostInput, void>('deletePost')

export interface SchedulePostsInPeriodInput {
  from: string
  to: string
}

export const schedulePostsInPeriod = callable<SchedulePostsInPeriodInput, void>(
  'schedulePostsInPeriod',
  { timeout: 310_000 },
)

export interface CreatePostInput {
  body?: string
  publishAt?: string
}

export interface CreatePostResult {
  postId: string
}

export const createPost = callable<CreatePostInput, CreatePostResult>('createPost')

export interface GetNextOpenSlotInput {
  postId: string
}

export const getNextOpenSlot = callable<GetNextOpenSlotInput, NextOpenSlot>('getNextOpenSlot')

export interface RestoreRevisionInput {
  postId: string
  revisionId: string
}

export interface RestoreRevisionResult {
  revisionId: string
}

/** Appends an earlier revision's body as a new revision on top of the current head. */
export const restoreRevision = callable<RestoreRevisionInput, RestoreRevisionResult>(
  'restoreRevision',
)

export interface AttachDesignToPostInput {
  postId: string
  designId: string
}

export const attachDesignToPost = callable<AttachDesignToPostInput, void>('attachDesignToPost')

export interface EditArticleMetaInput {
  postId: string
  titleTag?: string
  metaDescription?: string
  slug?: string
}

export const editArticleMeta = callable<EditArticleMetaInput, void>('editArticleMeta')

export interface AttachPublishedUrlInput {
  postId: string
  url: string
}

export const attachPublishedUrl = callable<AttachPublishedUrlInput, void>('attachPublishedUrl')

export const EXPORT_FORMATS = ['markdown', 'html'] as const

export type ExportFormat = (typeof EXPORT_FORMATS)[number]

export interface ExportArticleInput {
  postId: string
  format?: ExportFormat
}

export interface ExportArticleResult {
  format: ExportFormat
  filename: string
  content: string
  slug: string
  seoTitle: string
  metaDescription: string
}

export const exportArticle = callable<ExportArticleInput, ExportArticleResult>('exportArticle')

export interface PromoteArticleInput {
  articlePostId: string
}

export interface PromoteArticleResult {
  runId: string
}

/** Starts the post that promotes a verified article; refused until `ArticleDoc.verifiedAt` is set. */
export const promoteArticle = callable<PromoteArticleInput, PromoteArticleResult>('promoteArticle')

export interface ReadArticleLinksInput {
  postId: string
}

interface ArticleLinksDto extends Omit<ArticleLinks, 'checkedAt'> {
  checkedAt: string | null
}

const callReadArticleLinks = callable<ReadArticleLinksInput, ArticleLinksDto>('readArticleLinks')

/** Reads the article's text and its topic links resolved against the topic now. */
export async function readArticleLinks(input: ReadArticleLinksInput): Promise<ArticleLinks> {
  const result = await callReadArticleLinks(input)
  return { ...result, checkedAt: result.checkedAt === null ? null : new Date(result.checkedAt) }
}

export interface RecheckArticleHealthInput {
  postId: string
}

export interface RecheckArticleHealthResult {
  healthy: boolean
}

/** Reads the published page again and records which of its topic links it carries. */
export const recheckArticleHealth = callable<RecheckArticleHealthInput, RecheckArticleHealthResult>(
  'recheckArticleHealth',
)
